/**
 * Alluvial-style money flow from summary.flow payload.
 */
import React from 'react';
import { CHART_RAMP } from '../../design/tokens';

const CashflowAlluvialChart = ({ flow }) => {
  const income = flow?.income_monthly || 0;
  const links = flow?.links || [];

  if (!income || income <= 0 || !links.length) {
    return (
      <p className="text-body text-ink-muted py-12 text-center">
        Add income or spending to see the alluvial flow.
      </p>
    );
  }

  const byTarget = {};
  links.forEach((l) => {
    if (l.source === 'income') {
      byTarget[l.target] = (byTarget[l.target] || 0) + l.value;
    }
  });

  const essentials = byTarget.essentials || 0;
  const discretionary = byTarget.discretionary || 0;
  const investable = byTarget.investable || 0;
  const midTotal = essentials + discretionary + investable || 1;

  const midSegments = [
    { key: 'essentials', label: 'Essentials', amount: essentials, color: CHART_RAMP[0] },
    { key: 'discretionary', label: 'Discretionary', amount: discretionary, color: CHART_RAMP[1] },
    { key: 'investable', label: 'Investable', amount: investable, color: CHART_RAMP[2] },
  ].filter((s) => s.amount > 0);

  const outLinks = links.filter((l) => l.source !== 'income');
  const outSegments = outLinks.map((l, i) => ({
    label:
      l.target === 'bills'
        ? 'Bills and fixed'
        : l.target === 'lifestyle'
          ? 'Lifestyle'
          : l.target,
    amount: l.value,
    color: CHART_RAMP[(i + 3) % CHART_RAMP.length],
    widthPct: (l.value / midTotal) * 100,
  }));

  const fmt = (n) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

  const Band = ({ segments, bandTotal, height = 40 }) => {
    if (!segments.length) return null;
    return (
      <div className="flex w-full overflow-hidden rounded-md border border-hairline" style={{ height }}>
        {segments.map((seg) => {
          const pct = bandTotal > 0 ? (seg.amount / bandTotal) * 100 : 0;
          return (
            <div
              key={seg.key || seg.label}
              style={{ width: `${pct}%`, backgroundColor: seg.color }}
              className="min-w-[2px] flex items-center justify-center"
              title={`${seg.label}: ${fmt(seg.amount)}`}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex justify-between text-label text-ink-muted mb-1">
          <span>Income</span>
          <span className="figure text-ink">{fmt(income)}</span>
        </div>
        <div className="w-full h-10 rounded-md border border-hairline" style={{ backgroundColor: CHART_RAMP[0] }} />
      </div>

      {midSegments.length > 0 && (
        <div>
          <p className="text-label text-ink-muted mb-2">From income</p>
          <Band segments={midSegments} bandTotal={midTotal} />
          <ul className="mt-2 space-y-1">
            {midSegments.map((s) => (
              <li key={s.key} className="flex justify-between text-label text-ink-muted">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.label}
                </span>
                <span className="figure text-ink">{fmt(s.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {outSegments.length > 0 && (
        <div>
          <p className="text-label text-ink-muted mb-2">Outcomes</p>
          <div className="flex w-full overflow-hidden rounded-md border border-hairline h-10">
            {outSegments.map((seg) => (
              <div
                key={seg.label}
                style={{ width: `${seg.widthPct}%`, backgroundColor: seg.color }}
                className="min-w-[2px]"
                title={`${seg.label}: ${fmt(seg.amount)}`}
              />
            ))}
          </div>
          <ul className="mt-2 space-y-1">
            {outSegments.map((seg) => (
              <li key={seg.label} className="flex justify-between text-label text-ink-muted">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: seg.color }} />
                  {seg.label}
                </span>
                <span className="figure text-ink">{fmt(seg.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CashflowAlluvialChart;
